import type { SubscriptionTier } from '../types/subscription'

// Canonical tier list used across client and server
export const ALLOWED_TIERS = [
  'demo',
  'starter',
  'core_plus',
  'founding_member',
] as const satisfies SubscriptionTier[]

export type BillingCadence = 'weekly' | 'monthly'

// Pricing source of truth (USD). Founding members are grandfathered.
export const TIER_PRICING_USD: Record<BillingCadence, Record<SubscriptionTier, number>> = {
  monthly: {
    demo: 0,
    starter: 29.99,
    core_plus: 89.99,
    founding_member: 10.0,
  },
  weekly: {
    demo: 0,
    starter: 9.99,
    core_plus: 29.99,
    founding_member: 2.31,
  },
}

export function getTierPriceUsd(tier: SubscriptionTier, cadence: BillingCadence): number {
  return TIER_PRICING_USD[cadence]?.[tier] ?? 0
}

export const TIER_ORDER: Record<SubscriptionTier, number> = {
  demo: 0,
  starter: 1,
  founding_member: 1.5, // Same level as starter (core features, no AI)
  core_plus: 2,
}

// Centralized household limits (reflected in meal plan generation and UI constraints)
export const MAX_HOUSEHOLD_BY_TIER: Record<SubscriptionTier, number> = {
  demo: 2,
  starter: 2,
  founding_member: 2, // Same as starter for alpha testing
  core_plus: 8,
}

// Display helpers if needed by UI
export const DISPLAY_NAME: Record<SubscriptionTier, string> = {
  demo: 'Demo',
  starter: 'Family',
  founding_member: 'Founding Member',
  core_plus: 'Master Chef',
}

// Normalize arbitrary inputs into the canonical tier set
export function normalizeTier(input?: string | null): SubscriptionTier {
  const v = String(input || '').trim().toLowerCase()
  if (v === 'starter' || v === 'duo' || v === 'kitchen_apprentice') return 'starter'
  if (v === 'core_plus' || v === 'core+' || v === 'master_chef' || v === 'master chef' || v === 'pro' || v === 'core') return 'core_plus'
  if (v === 'demo' || v === 'free') return 'demo'
  if (v === 'founding_member' || v === 'founder' || v === 'alpha') return 'founding_member'
  // Fallback: default to starter for unknown/legacy values
  return 'starter'
}

export function compareTiers(a: SubscriptionTier, b: SubscriptionTier): number {
  return TIER_ORDER[a] - TIER_ORDER[b]
}

export function canAccess(required: SubscriptionTier, actual: SubscriptionTier): boolean {
  return compareTiers(actual, required) >= 0
}

// Feature flags by tier (centralized)
// Founding members get same access as core_plus (locked at $10/month forever)
export const FEATURES_BY_TIER: Record<SubscriptionTier, { ai: boolean }> = {
  demo: { ai: false },
  starter: { ai: false },
  founding_member: { ai: true }, // Same access as core_plus, locked at $10/month
  core_plus: { ai: true },
}

export function isAIEnabled(tier: SubscriptionTier): boolean {
  return !!FEATURES_BY_TIER[tier]?.ai
}
